import { Button, Grid, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import RegionsDropdown from '../../../../components/RegionsDropdown';
import ZoneDropdown from '../../../../components/ZonesDropdown';
import CentersDropdown from '../../../../components/CentersDropdown';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../../hooks/useAuth';
import {
	ApplicationTableDataType,
	RegionZoneCenterParams,
} from '../../../../shared/VolunteerApplicationServiceTypes';
import ConfirmTick from '../../../../assets/images/confirm-tick.svg';
import { useInfoPopup } from '../../../../hooks/useInfoPopUp';
import { SCREENING_DASHBOARD } from '../../../../shared/Routes';
import { useBatchId } from '../../../../hooks/useBatchId';
import { useZoneChange } from './useZoneChange';

interface ZoneChangeProps {
	open: boolean;
	onClose: () => void;
	application: ApplicationTableDataType | undefined;
}

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface OptionData {
	value: number | undefined;
	label: string | undefined;
}

interface ZoneOption {
	value: string;
	label: string;
}

export const ZoneChangePopup = ({
	open,
	onClose,
	application,
}: ZoneChangeProps) => {
	const { t, i18n } = useTranslation();
	const { srcmId } = useAuth();
	const { navigateToRoute } = useBatchId();
	const { updateRegionZoneCenterZoneChange } = useZoneChange();
	const { infoPopUpAction } = useInfoPopup();
	const [regions, setRegions] = useState<OptionData>();
	const [zones, setZones] = useState<OptionData>();
	const [centers, setCenters] = useState<OptionData>();
	const [initialValues, setInitialValues] = useState<{
		region?: OptionData;
		zone?: OptionData;
		center?: OptionData;
	}>({});
	const [isRegionOrZoneOrCenters, setIsRegionOrZoneOrCenters] =
		useState<boolean>(true);
	const lengthyLanguages = ['ta', 'ma', 'te', 'ka'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;

	const handleRegionsChange = useCallback((value: OptionData) => {
		setRegions(value);
		setZones(undefined);
		setCenters(undefined);
		setIsRegionOrZoneOrCenters(false);
	}, []);

	const handleZoneChange = useCallback(
		(value: ZoneOption[] | ZoneOption | null | undefined) => {
			const arr = Array.isArray(value) ? value : value ? [value] : [];
			const selectedValue = arr[0];

			if (!selectedValue) {
				setZones(undefined);
				setCenters(undefined);
				setIsRegionOrZoneOrCenters(false);
				return;
			}

			setZones({
				value: Number(selectedValue.value),
				label: selectedValue.label,
			});
			setCenters(undefined);
			setIsRegionOrZoneOrCenters(false);
		},
		[]
	);

	const handleCenterChange = useCallback((value: OptionData) => {
		setCenters(value);
		setIsRegionOrZoneOrCenters(false);
	}, []);

	const handleZoneChangeClick = useCallback(async () => {
		const originalAppId = application?.originalApplication?.applicationId;
		const regionId = regions?.value;
		const regionName = regions?.label;
		const zoneId = zones?.value;
		const zoneName = zones?.label;
		const centerId = centers?.value;
		const centerName = centers?.label;

		if (
			!originalAppId ||
			!regionId ||
			!regionName ||
			!zoneId ||
			!zoneName ||
			!centerId ||
			!centerName
		) {
			return;
		}

		const updateParams: RegionZoneCenterParams = {
			applicationId: originalAppId,
			regionIdToBeUpdated: regionId,
			regionNameToBeUpdated: regionName,
			zoneIdToBeUpdated: zoneId,
			zoneNameToBeUpdated: zoneName,
			centerIdToBeUpdated: centerId,
			centerNameToBeUpdated: centerName,
		};

		const updatedZone = await updateRegionZoneCenterZoneChange(updateParams);

		if (updatedZone?.centerChanged) {
			onClose();
			infoPopUpAction(
				updatedZone.proposerReassigned
					? 'zoneProposerReassignedSuccessfully'
					: 'zoneChangeSuccessfully',
				'',
				() => {
					navigateToRoute(SCREENING_DASHBOARD);
				},
				ConfirmTick
			);
		}
	}, [
		application?.originalApplication?.applicationId,
		centers,
		infoPopUpAction,
		navigateToRoute,
		onClose,
		regions,
		updateRegionZoneCenterZoneChange,
		zones,
	]);

	useEffect(() => {
		if (application?.originalApplication.center) {
			setRegions({
				label: application?.originalApplication.center.region,
				value: application?.originalApplication.center.regionId,
			});
			setZones({
				label: application.originalApplication.center.zone,
				value: application.originalApplication.center.zoneId,
			});
			setCenters({
				label: application.originalApplication.center.name,
				value: Number(application.originalApplication.center.externalId),
			});
			setInitialValues({
				region: {
					label: application?.originalApplication.center.region,
					value: application?.originalApplication.center.regionId,
				},
				zone: {
					label: application.originalApplication.center.zone,
					value: application.originalApplication.center.zoneId,
				},
				center: {
					label: application.originalApplication.center.name,
					value: Number(application.originalApplication.center.externalId),
				},
			});
		} else {
			setRegions(undefined);
			setZones(undefined);
			setCenters(undefined);
			setInitialValues({});
		}
	}, [application?.originalApplication.center]);

	useEffect(() => {
		const hasAllValues = regions && zones && centers;

		if (!hasAllValues) {
			setIsRegionOrZoneOrCenters(true);
			return;
		}

		const isSameRegion = regions?.value == initialValues.region?.value;

		const isSameZone = zones?.value == initialValues.zone?.value;

		const isSameCenter = centers?.value == initialValues.center?.value;

		const isAllSame = isSameRegion && isSameZone && isSameCenter;

		setIsRegionOrZoneOrCenters(isAllSame);
	}, [regions, zones, centers, initialValues]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-4xl md:min-w-[700px]'
			}
			closable={true}
			maskClosable={true}
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('zoneChange')}
			</div>
			<div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-4 mt-5">
				<div className="w-full mb-[16px]">
					<Text className="block text-md text-black font-bold">Note:-</Text>
					<Text className="block text-md text-black font-bold">
						{t('zoneChangeWarningMessage')}
					</Text>
				</div>
				<div className="w-full">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">{t('region')}</Text>
						<RegionsDropdown
							functionarySrcmId={srcmId}
							value={regions}
							onChange={handleRegionsChange}
							getRegionChange={true}
						/>
					</div>
				</div>
				<div className="w-full">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">{t('zone')}</Text>
						<ZoneDropdown
							functionarySrcmId={srcmId}
							value={
								zones?.value
									? [
											{
												value: String(zones.value),
												label: zones.label ?? String(zones.value),
											},
									  ]
									: []
							}
							disabled={!regions?.value}
							onChange={handleZoneChange}
							regionId={regions?.value}
							key={regions?.value}
							getZoneChange={true}
						/>
					</div>
				</div>
				<div className="w-full">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">{t('center')}</Text>
						<CentersDropdown
							functionarySrcmId={srcmId}
							value={centers}
							onChange={handleCenterChange}
							zoneValue={zones?.label}
							disabled={!zones?.label}
						/>
					</div>
				</div>
			</div>

			<div
				className={`flex ${
					isStackVertical
						? 'flex-col items-center space-y-2'
						: 'justify-end gap-2'
				} !mt-10 ant-modal-footer`}
			>
				<Button
					className={`h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? '!ms-0 p-2 w-full' : 'w-[162px]'
					}`}
					onClick={onClose}
				>
					{t('cancel')}
				</Button>
				<Button
					type="primary"
					onClick={() => {
						void handleZoneChangeClick();
					}}
					className={`h-9 font-sans font-bold ${
						isStackVertical ? '!ms-0 p-2 mt-3 w-full' : 'w-[162px]'
					}`}
					disabled={isRegionOrZoneOrCenters}
				>
					{t('change')}
				</Button>
			</div>
		</Modal>
	);
};
